import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    PhoneCall, Volume2, ClipboardList, FlaskConical, MessageCircle, Settings, Sparkles, FileCheck, Plug, Globe, PhoneOutgoing, Languages, Share2, ListChecks, Workflow, SpellCheck, CalendarClock, UserRound, MessageSquareQuote, PhoneIncoming,
} from 'lucide-react';
import { ApiIntegrationModal } from './ApiIntegrationModal.jsx';
import { FlowViewerModal } from './FlowViewerModal.jsx';
import { LanguagePickerModal } from './LanguagePickerModal.jsx';
import { LeadFormModal } from './LeadFormModal.jsx';
import { DialerListModal } from './DialerListModal.jsx';
import { SmartExtractModal } from './SmartExtractModal.jsx';
import { StyleModal } from './StyleModal.jsx';
import { SystemPromptModal } from './SystemPromptModal.jsx';
import { KeywordTuningModal } from './KeywordTuningModal.jsx';
import { VoicePickerModal } from './VoicePickerModal.jsx';
import { GreetingScheduleModal } from './GreetingScheduleModal.jsx';
import { PreviewCallModal } from './PreviewCall/PreviewCallModal.jsx';
import { AutoTestModal } from './AutoTestModal.jsx';
import { ShareLinkModal } from './ShareLinkModal.jsx';
import { VirtualHumanModal } from './VirtualHumanModal.jsx';
import { SurveyQuestionsModal } from './SurveyQuestionsModal.jsx';
import { SurveyFlowEditorModal } from './SurveyFlowEditorModal.jsx';
import { CannedMessagesModal } from './CannedMessagesModal.jsx';
import { CallBehaviorModal } from './CallBehaviorModal.jsx';
import { useTenant } from '../../TenantContext.jsx';

/**
 * WorkshopPane — 右欄快捷動作
 *
 * Actions:
 *   - 模擬通話 / 語音 / 收單情境：Phase 3 模態待補
 *   - 模擬對話 / 自動情境測試 / 同步部署：sendMessage 觸發 skill
 *   - 對話風格：開 StyleModal → 套用後 sendMessage 觸發 sync-ivr-config
 *   - Flow 設定：開 FlowViewerModal 看 agentInstruction + 節點結構
 */
export function WorkshopPane({ sendMessage, isStreaming, avatarId, avatarName, spaceId, currentVoiceId, currentProvider = 'gemini', currentFirstMessage, currentLanguage, currentMultiLingual, currentKeyword = [], currentVoiceCorrectionWords = [], currentBrandColor = '', currentOrbLabel = '', currentOrbImageUrl = '', hostId, did, testDialerSetup = null, readOnly = false, newMode = null, flowMode = null, ready = true }) {
    const { t } = useTranslation('workspace');
    const [styleOpen, setStyleOpen] = useState(false);
    const [keywordOpen, setKeywordOpen] = useState(false);
    const [flowOpen, setFlowOpen] = useState(false);
    const [voiceOpen, setVoiceOpen] = useState(false);
    const [langOpen, setLangOpen] = useState(false);
    const [systemPromptOpen, setSystemPromptOpen] = useState(false);
    const [previewCallOpen, setPreviewCallOpen] = useState(false);
    const [leadFormOpen, setLeadFormOpen] = useState(false);
    const [dialerListOpen, setDialerListOpen] = useState(false);
    const [smartExtractOpen, setSmartExtractOpen] = useState(false);
    const [autoTestOpen, setAutoTestOpen] = useState(false);
    const [apiIntegrationOpen, setApiIntegrationOpen] = useState(false);
    const [shareLinkOpen, setShareLinkOpen] = useState(false);
    const [virtualHumanOpen, setVirtualHumanOpen] = useState(false);
    const [surveyQuestionsOpen, setSurveyQuestionsOpen] = useState(false);
    const [surveyFlowOpen, setSurveyFlowOpen] = useState(false);
    const [greetingScheduleOpen, setGreetingScheduleOpen] = useState(false);
    const [cannedOpen, setCannedOpen] = useState(false);
    const [callBehaviorOpen, setCallBehaviorOpen] = useState(false);

    const isSurvey = newMode === 'survey';

    // ── Survey 工作室(問卷題目/流程置頂,沒 share-link/撥號名單) ──
    const surveyActions = [
        { key: 'preview-call', icon: PhoneCall, label: t('action.previewCall.label'), sub: t('action.previewCall.sub'),
          onClick: () => setPreviewCallOpen(true) },
        { key: 'survey-questions', icon: ListChecks, label: t('action.surveyQuestions.label'), sub: t('action.surveyQuestions.sub'),
          onClick: () => setSurveyQuestionsOpen(true) },
        { key: 'survey-flow', icon: Workflow, label: t('action.surveyFlow.label'), sub: t('action.surveyFlow.sub'),
          onClick: () => setSurveyFlowOpen(true) },
        { key: 'system-prompt', icon: FileCheck, label: t('action.systemPrompt.label'), sub: t('action.systemPrompt.sub'),
          onClick: () => setSystemPromptOpen(true) },
        { key: 'keyword-tuning', icon: SpellCheck, label: t('action.keywordTuning.label'), sub: t('action.keywordTuning.sub'),
          onClick: () => setKeywordOpen(true) },
        { key: 'language', icon: Languages, label: t('action.language.label'), sub: t('action.language.sub'),
          onClick: () => setLangOpen(true) },
        { key: 'voice', icon: Volume2, label: t('action.voice.label'), sub: t('action.voice.sub'),
          onClick: () => setVoiceOpen(true) },
        { key: 'style', icon: Sparkles, label: t('action.style.label'), sub: t('action.style.sub'),
          onClick: () => setStyleOpen(true) },
        { key: 'auto-test', icon: FlaskConical, label: t('action.autoTest.label'), sub: t('action.autoTest.sub'),
          onClick: () => setAutoTestOpen(true) },
    ];

    // ── IVR 工作室(原本 12 顆,不動) ──
    const ivrActions = [
        { key: 'preview-call', icon: PhoneCall, label: t('action.previewCall.label'), sub: t('action.previewCall.sub'),
          onClick: () => setPreviewCallOpen(true) },
        { key: 'share-link', icon: Share2, label: t('action.shareLink.label'), sub: t('action.shareLink.sub'),
          onClick: () => setShareLinkOpen(true) },
        { key: 'canned-messages', icon: MessageSquareQuote, label: t('action.cannedMessages.label'), sub: t('action.cannedMessages.sub'),
          onClick: () => setCannedOpen(true) },
        { key: 'greeting-schedule', icon: CalendarClock, label: t('action.greetingSchedule.label'), sub: t('action.greetingSchedule.sub'),
          onClick: () => setGreetingScheduleOpen(true) },
        // 通話行為（0908 君綺）：能不能插話 / 客戶不出聲時 / 開場白後沒出聲。以前只有精靈看得到。
        { key: 'call-behavior', icon: PhoneIncoming, label: t('action.callBehavior.label'), sub: t('action.callBehavior.sub'),
          onClick: () => setCallBehaviorOpen(true) },
        { key: 'system-prompt', icon: FileCheck, label: t('action.systemPrompt.label'), sub: t('action.systemPrompt.sub'),
          onClick: () => setSystemPromptOpen(true) },
        { key: 'keyword-tuning', icon: SpellCheck, label: t('action.keywordTuning.label'), sub: t('action.keywordTuning.sub'),
          onClick: () => setKeywordOpen(true) },
        { key: 'language', icon: Languages, label: t('action.language.label'), sub: t('action.language.sub'),
          onClick: () => setLangOpen(true) },
        { key: 'voice', icon: Volume2, label: t('action.voice.label'), sub: t('action.voice.sub'),
          onClick: () => setVoiceOpen(true) },
        { key: 'virtual-human', icon: UserRound, label: t('action.virtualHuman.label'), sub: t('action.virtualHuman.sub'),
          onClick: () => setVirtualHumanOpen(true) },
        { key: 'style', icon: Sparkles, label: t('action.style.label'), sub: t('action.style.sub'),
          onClick: () => setStyleOpen(true) },
        { key: 'leads', icon: ClipboardList, label: t('action.leads.label'), sub: t('action.leads.sub'),
          onClick: () => setLeadFormOpen(true) },
        { key: 'dialer-list', icon: PhoneOutgoing, label: t('action.dialerList.label'), sub: t('action.dialerList.sub'),
          onClick: () => setDialerListOpen(true) },
        { key: 'smart-extract', icon: Globe, label: t('action.smartExtract.label'), sub: t('action.smartExtract.sub'),
          onClick: () => setSmartExtractOpen(true) },
        { key: 'api-integration', icon: Plug, label: t('action.apiIntegration.label'), sub: t('action.apiIntegration.sub'),
          onClick: () => setApiIntegrationOpen(true) },
        { key: 'flow-config', icon: Settings, label: t('action.flowConfig.label'), sub: t('action.flowConfig.sub'),
          onClick: () => setFlowOpen(true) },
        { key: 'auto-test', icon: FlaskConical, label: t('action.autoTest.label'), sub: t('action.autoTest.sub'),
          onClick: () => setAutoTestOpen(true) },
    ];

    // AI 總機（transfer-only）：純轉接用不到收單/外撥/擷取/API/語速/發音調校
    const TRANSFER_ONLY_HIDDEN = new Set(['leads', 'dialer-list', 'smart-extract', 'api-integration', 'style', 'keyword-tuning']);
    // 帳號無 CS（智慧客服）席次：CS 專屬動作一律隱藏（不論 avatar flowMode，涵蓋既有 full-flow avatar）
    const CS_ONLY = new Set(['leads', 'dialer-list', 'smart-extract', 'api-integration', 'style', 'keyword-tuning',
        'virtual-human', 'share-link', 'canned-messages', 'survey-questions', 'survey-flow']);
    const { hasCS } = useTenant();

    const baseActions = isSurvey ? surveyActions : ivrActions;
    let actions = flowMode === 'transfer-only'
        ? baseActions.filter((a) => !TRANSFER_ONLY_HIDDEN.has(a.key))
        : baseActions;
    if (!hasCS) actions = actions.filter((a) => !CS_ONLY.has(a.key));

    // 子帳號（readOnly）：只開放「模擬通話」，其餘編輯動作全部隱藏
    const visibleActions = readOnly ? actions.filter((a) => a.key === 'preview-call') : actions;

    // avatar 還沒載完 → render skeleton（避免「IVR 版按鈕 → flicker → survey 版按鈕」）
    // 用同樣 2-col grid + 10 個灰塊 placeholder,維持 layout 不跳
    if (!ready) {
        return (
            <div className="flex flex-col h-full min-h-0 bg-cream-100/40">
                <div className="flex-1 overflow-y-auto p-2.5">
                    <div className="grid grid-cols-2 gap-2">
                        {Array.from({ length: 10 }).map((_, idx) => (
                            <div
                                key={idx}
                                className="relative flex flex-col items-start gap-2.5 p-3 rounded-md bg-white border border-cream-200 min-h-[112px] overflow-hidden"
                            >
                                <span className="absolute top-2 right-2.5 font-mono text-[9px] tracking-wider text-ink-200 tabular-nums">
                                    {String(idx + 1).padStart(2, '0')}
                                </span>
                                <div className="size-8 rounded-md bg-cream-100 animate-pulse" />
                                <div className="w-2/3 h-3 rounded bg-cream-100 animate-pulse" />
                                <div className="w-4/5 h-2 rounded bg-cream-100 animate-pulse" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="flex flex-col h-full min-h-0 bg-cream-100/40">
                <div className="flex-1 overflow-y-auto p-2.5">
                    <div className="grid grid-cols-2 gap-2">
                        {visibleActions.map((a, idx) => {
                            const Icon = a.icon;
                            const isDisabled = a.disabled || (a.onClick && isStreaming);
                            return (
                                <button
                                    key={a.key}
                                    onClick={() => a.onClick && a.onClick()}
                                    disabled={isDisabled}
                                    title={a.tooltip || a.sub}
                                    className={[
                                        'group relative flex flex-col items-start gap-2.5 p-3 rounded-md text-left transition-all duration-200 min-h-[112px]',
                                        isDisabled
                                            ? 'bg-cream-50/50 text-ink-400 cursor-not-allowed border border-cream-100'
                                            : 'bg-white border border-cream-200 hover:border-accent-400 hover:shadow-[0_2px_12px_rgba(168,144,96,0.12)] hover:-translate-y-0.5 text-ink-900',
                                    ].join(' ')}
                                >
                                    {/* index dot — editorial accent */}
                                    <span
                                        className={[
                                            'absolute top-2 right-2.5 font-mono text-[9px] tracking-wider tabular-nums',
                                            isDisabled ? 'text-ink-300' : 'text-ink-300 group-hover:text-accent-600',
                                        ].join(' ')}
                                    >
                                        {String(idx + 1).padStart(2, '0')}
                                    </span>

                                    {/* Icon */}
                                    <div
                                        className={[
                                            'inline-flex items-center justify-center size-8 rounded-md transition-colors',
                                            isDisabled
                                                ? 'bg-cream-100/50'
                                                : 'bg-cream-100 group-hover:bg-accent-50',
                                        ].join(' ')}
                                    >
                                        <Icon
                                            size={17}
                                            strokeWidth={1.6}
                                            className={isDisabled ? 'text-ink-300' : 'text-accent-700'}
                                        />
                                    </div>

                                    {/* Label + sub */}
                                    <div className="flex-1 min-w-0 w-full">
                                        <div className="text-sm font-medium leading-tight">{a.label}</div>
                                        <div className="text-[11px] text-ink-400 mt-1 leading-snug line-clamp-2">
                                            {a.sub}
                                        </div>
                                    </div>

                                    {a.disabled && (
                                        <span className="absolute bottom-2 right-2.5 font-mono text-[8px] uppercase tracking-wider text-ink-300">
                                            soon
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            <StyleModal
                open={styleOpen}
                onClose={() => setStyleOpen(false)}
                onApply={sendMessage}
                isStreaming={isStreaming}
                avatarName={avatarName}
                newMode={newMode}
            />
            <FlowViewerModal
                open={flowOpen}
                onClose={() => setFlowOpen(false)}
                avatarId={avatarId}
                avatarName={avatarName}
            />
            <VoicePickerModal
                open={voiceOpen}
                onClose={() => setVoiceOpen(false)}
                onApply={sendMessage}
                isStreaming={isStreaming}
                avatarName={avatarName}
                avatarId={avatarId}
                currentVoiceId={currentVoiceId}
                currentFirstMessage={currentFirstMessage}
                newMode={newMode}
                provider={currentProvider}
            />
            <GreetingScheduleModal
                open={greetingScheduleOpen}
                onClose={() => setGreetingScheduleOpen(false)}
                avatarId={avatarId}
                avatarName={avatarName}
                currentFirstMessage={currentFirstMessage}
                sendMessage={sendMessage}
                isStreaming={isStreaming}
            />
            <CannedMessagesModal
                open={cannedOpen}
                onClose={() => setCannedOpen(false)}
                avatarId={avatarId}
            />
            <CallBehaviorModal
                open={callBehaviorOpen}
                onClose={() => setCallBehaviorOpen(false)}
                avatarId={avatarId}
            />
            <LanguagePickerModal
                open={langOpen}
                onClose={() => setLangOpen(false)}
                onApply={sendMessage}
                isStreaming={isStreaming}
                avatarName={avatarName}
                currentLanguage={currentLanguage}
                currentMultiLingual={currentMultiLingual}
                newMode={newMode}
            />
            <SystemPromptModal
                open={systemPromptOpen}
                onClose={() => setSystemPromptOpen(false)}
                onApply={sendMessage}
                isStreaming={isStreaming}
                avatarName={avatarName}
                newMode={newMode}
            />
            <KeywordTuningModal
                open={keywordOpen}
                onClose={() => setKeywordOpen(false)}
                onApply={sendMessage}
                isStreaming={isStreaming}
                avatarName={avatarName}
                currentKeyword={currentKeyword}
                currentVoiceCorrectionWords={currentVoiceCorrectionWords}
            />
            <PreviewCallModal
                open={previewCallOpen}
                onClose={() => setPreviewCallOpen(false)}
                avatarId={avatarId}
                hostId={hostId}
                did={did}
                avatarName={avatarName}
                primaryLanguage={currentLanguage}
                testDialerSetup={testDialerSetup}
            />
            <LeadFormModal
                open={leadFormOpen}
                onClose={() => setLeadFormOpen(false)}
                onApply={sendMessage}
                isStreaming={isStreaming}
                avatarName={avatarName}
            />
            <DialerListModal
                open={dialerListOpen}
                onClose={() => setDialerListOpen(false)}
                onApply={sendMessage}
                isStreaming={isStreaming}
                avatarId={avatarId}
                avatarName={avatarName}
            />
            <SmartExtractModal
                open={smartExtractOpen}
                onClose={() => setSmartExtractOpen(false)}
                onApply={sendMessage}
                isStreaming={isStreaming}
                avatarName={avatarName}
            />
            <AutoTestModal
                open={autoTestOpen}
                onClose={() => setAutoTestOpen(false)}
                onApply={sendMessage}
                isStreaming={isStreaming}
                avatarName={avatarName}
                newMode={newMode}
            />
            <ApiIntegrationModal
                open={apiIntegrationOpen}
                onClose={() => setApiIntegrationOpen(false)}
                onApply={sendMessage}
                isStreaming={isStreaming}
                avatarName={avatarName}
            />
            <ShareLinkModal
                open={shareLinkOpen}
                onClose={() => setShareLinkOpen(false)}
                spaceId={spaceId}
                avatarId={avatarId}
                avatarName={avatarName}
                currentBrandColor={currentBrandColor}
                currentOrbLabel={currentOrbLabel}
                currentOrbImageUrl={currentOrbImageUrl}
            />
            <VirtualHumanModal
                open={virtualHumanOpen}
                onClose={() => setVirtualHumanOpen(false)}
                avatarId={avatarId}
                avatarName={avatarName}
                spaceId={spaceId}
                defaultLanguage={currentLanguage}
            />
            <SurveyQuestionsModal
                open={surveyQuestionsOpen}
                onClose={() => setSurveyQuestionsOpen(false)}
                avatarId={avatarId}
                avatarName={avatarName}
                sendMessage={sendMessage}
                isStreaming={isStreaming}
            />
            <SurveyFlowEditorModal
                open={surveyFlowOpen}
                onClose={() => setSurveyFlowOpen(false)}
                avatarId={avatarId}
                avatarName={avatarName}
            />
        </>
    );
}
